import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';

const FilterBarContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

const TopRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const ActiveFiltersRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  minHeight: '32px',
}));

/**
 * Compact Filter Bar Component
 * Modern, space-efficient filter interface with progressive disclosure
 *
 * Features:
 * - Integrated search field (left side)
 * - Sets dropdown for quick filter combinations
 * - "+ Filter hinzufügen" button to toggle expansion panel
 * - AI Assistant button for intelligent filtering
 * - Name view toggle (Lokal, OMOP, Beide) - right aligned
 * - Active filters displayed as removable chips (including AI suggestions)
 * - ~120px height vs ~600px of old design
 *
 * @param {string} searchTerm - Current search term
 * @param {Function} setSearchTerm - Setter for search term
 * @param {Array} savedSets - Available filter sets
 * @param {Function} handleLoadSet - Handler for loading a saved set
 * @param {Function} getSetTypeBadge - Get badge label for set type
 * @param {Array} selectedTags - Selected tag filters
 * @param {Array} selectedStations - Selected station filters
 * @param {string} selectedSensitivity - Selected sensitivity filter
 * @param {Function} setSelectedTags - Setter for tags
 * @param {Function} setSelectedStations - Setter for stations
 * @param {Function} setSelectedSensitivity - Setter for sensitivity
 * @param {Array} aiSuggestedParameterIds - AI-suggested parameter IDs
 * @param {Function} setAiSuggestedParameterIds - Setter for AI suggestions
 * @param {Array} parameters - All parameters (to get names for AI chips)
 * @param {boolean} filterPanelOpen - Whether filter expansion panel is open
 * @param {Function} setFilterPanelOpen - Toggle filter expansion panel
 * @param {Function} setAiAssistantOpen - Open AI assistant dialog
 * @param {string} nameView - Current name view mode (charite, omop, both)
 * @param {Function} setNameView - Setter for name view mode
 * @param {Function} clearAllFilters - Handler to clear all filters
 */
const CompactFilterBar = ({
  searchTerm,
  setSearchTerm,
  savedSets,
  handleLoadSet,
  getSetTypeBadge,
  selectedTags,
  selectedStations,
  selectedSensitivity,
  selectedOmopStatus,
  selectedSourceSystems,
  aiSuggestedParameterIds,
  setParameterIds,
  setSelectedTags,
  setSelectedStations,
  setSelectedSensitivity,
  setSelectedOmopStatus,
  setSelectedSourceSystems,
  setAiSuggestedParameterIds,
  setSetParameterIds,
  parameters,
  getParameterName,
  filterPanelOpen,
  setFilterPanelOpen,
  setAiAssistantOpen,
  nameView,
  setNameView,
  clearAllFilters,
}) => {
  // Menu anchor for sets
  const [setsMenuAnchor, setSetsMenuAnchor] = useState(null);

  // Calculate active filter count
  const activeFilterCount =
    selectedTags.length +
    selectedStations.length +
    (selectedSensitivity && selectedSensitivity !== 'all' ? 1 : 0) +
    (selectedOmopStatus && selectedOmopStatus !== 'all' ? 1 : 0) +
    (selectedSourceSystems ? selectedSourceSystems.length : 0) +
    (aiSuggestedParameterIds ? aiSuggestedParameterIds.length : 0) +
    (setParameterIds ? setParameterIds.length : 0);

  // Handlers for menus
  const handleOpenSetsMenu = (event) => {
    setSetsMenuAnchor(event.currentTarget);
  };

  const handleCloseSetsMenu = () => {
    setSetsMenuAnchor(null);
  };

  const handleLoadSetClick = (set) => {
    handleLoadSet(set);
    handleCloseSetsMenu();
  };

  const handleToggleFilterPanel = () => {
    setFilterPanelOpen(!filterPanelOpen);
  };

  // Remove individual tag
  const handleRemoveTag = (tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  // Remove individual station
  const handleRemoveStation = (station) => {
    setSelectedStations(prev => prev.filter(s => s !== station));
  };

  // Remove sensitivity filter
  const handleRemoveSensitivity = () => {
    setSelectedSensitivity('all');
  };

  // Remove OMOP status filter
  const handleRemoveOmopStatus = () => {
    setSelectedOmopStatus('all');
  };

  // Remove individual source system
  const handleRemoveSourceSystem = (system) => {
    setSelectedSourceSystems(prev => prev.filter(s => s !== system));
  };

  // Remove individual AI suggested parameter
  const handleRemoveAiSuggestion = (parameterId) => {
    setAiSuggestedParameterIds(prev => prev.filter(id => id !== parameterId));
  };

  // Remove individual set parameter
  const handleRemoveSetParameter = (parameterId) => {
    setSetParameterIds(prev => prev.filter(id => id !== parameterId));
  };

  // Get parameter name by ID (use prop function if provided)
  const getParamName = getParameterName || ((parameterId) => {
    const param = parameters?.find(p => p.id === parameterId);
    return param ? param.name : `Parameter ${parameterId}`;
  });

  // Get label for sensitivity
  const getSensitivityLabel = (value) => {
    switch(value) {
      case 'plain': return 'Plain';
      case 'pseudonymisiert': return 'Pseudonymisiert';
      case 'anonymisiert': return 'Anonymisiert';
      default: return value;
    }
  };

  // Get label for OMOP status
  const getOmopStatusLabel = (value) => {
    switch(value) {
      case 'validated': return 'Validiert';
      case 'suggested': return 'Vorgeschlagen';
      case 'not_mapped': return 'Nicht gemappt';
      default: return value;
    }
  };

  // Filter sets by type (for Sets menu)
  const parameterSets = savedSets?.filter(set => set.type === 'parameters' || set.type === 'mixed') || [];

  return (
    <FilterBarContainer elevation={1}>
      {/* Top Row: Search + Action Buttons */}
      <TopRow>
        {/* Search Field */}
        <TextField
          placeholder="Parameter suchen... (z.B. 'Blutdruck', 'Ventilation', 'Labor')"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            flexGrow: 1,
            minWidth: '300px',
            maxWidth: '600px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Sets Dropdown */}
        <Button
          variant="outlined"
          startIcon={<BookmarkIcon />}
          endIcon={<ExpandMoreIcon />}
          onClick={handleOpenSetsMenu}
          size="medium"
        >
          Sets
        </Button>
        <Menu
          anchorEl={setsMenuAnchor}
          open={Boolean(setsMenuAnchor)}
          onClose={handleCloseSetsMenu}
          PaperProps={{
            sx: { minWidth: 280 }
          }}
        >
          <MenuItem disabled>
            <Typography variant="caption" color="textSecondary">
              Gespeicherte Filter-Kombinationen
            </Typography>
          </MenuItem>
          <Divider />
          {parameterSets.length > 0 ? (
            parameterSets.map(set => (
              <MenuItem key={set.id} onClick={() => handleLoadSetClick(set)}>
                <ListItemIcon>
                  <span style={{ fontSize: '1.2rem' }}>{set.icon}</span>
                </ListItemIcon>
                <ListItemText
                  primary={set.name}
                  secondary={getSetTypeBadge(set.type)}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">
                Keine Sets verfügbar
              </Typography>
            </MenuItem>
          )}
        </Menu>

        {/* Add Filter Button */}
        <Badge badgeContent={activeFilterCount} color="primary">
          <Button
            variant={filterPanelOpen ? "contained" : "outlined"}
            startIcon={<AddIcon />}
            endIcon={<ExpandMoreIcon sx={{ transform: filterPanelOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }} />}
            onClick={handleToggleFilterPanel}
            size="medium"
          >
            Filter hinzufügen
          </Button>
        </Badge>

        {/* AI Assistant Button */}
        <Button
          variant="contained"
          size="medium"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setAiAssistantOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
            }
          }}
        >
          KI-Assistent
        </Button>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Name View Toggle */}
        <ToggleButtonGroup
          value={nameView}
          exclusive
          onChange={(e, newView) => newView && setNameView(newView)}
          size="small"
          sx={{ backgroundColor: 'white' }}
        >
          <ToggleButton value="charite">
            <Tooltip title="Zeige lokale Namen als Hauptname">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                Lokal
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="omop">
            <Tooltip title="Zeige OMOP-Namen als Hauptname">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                OMOP
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="both">
            <Tooltip title="Zeige beide Namen">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                <SwapHorizIcon fontSize="small" />
                Beide
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Reset Button */}
        {activeFilterCount > 0 && (
          <Button
            size="small"
            onClick={clearAllFilters}
            color="secondary"
            sx={{ ml: 1 }}
          >
            Alle zurücksetzen
          </Button>
        )}
      </TopRow>

      {/* Active Filters Row */}
      {activeFilterCount > 0 && (
        <ActiveFiltersRow>
          <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
            Aktive Filter ({activeFilterCount}):
          </Typography>

          {/* Tag Chips */}
          {selectedTags.map(tag => (
            <Chip
              key={`tag-${tag}`}
              label={tag}
              size="small"
              color="primary"
              onDelete={() => handleRemoveTag(tag)}
              icon={<LocalOfferIcon />}
            />
          ))}

          {/* Station Chips */}
          {selectedStations.map(station => (
            <Chip
              key={`station-${station}`}
              label={station}
              size="small"
              color="secondary"
              onDelete={() => handleRemoveStation(station)}
              icon={<LocalHospitalIcon />}
            />
          ))}

          {/* Source System Chips */}
          {selectedSourceSystems && selectedSourceSystems.map(system => (
            <Chip
              key={`system-${system}`}
              label={system}
              size="small"
              color="info"
              onDelete={() => handleRemoveSourceSystem(system)}
              icon={<StorageIcon />}
            />
          ))}

          {/* Set Parameter Chips */}
          {setParameterIds && setParameterIds.map(paramId => (
            <Chip
              key={`set-${paramId}`}
              label={getParamName(paramId)}
              size="small"
              onDelete={() => handleRemoveSetParameter(paramId)}
              icon={<BookmarkIcon />}
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: 'white',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ))}

          {/* AI Suggested Parameter Chips */}
          {aiSuggestedParameterIds && aiSuggestedParameterIds.map(paramId => (
            <Chip
              key={`ai-${paramId}`}
              label={getParamName(paramId)}
              size="small"
              onDelete={() => handleRemoveAiSuggestion(paramId)}
              icon={<AutoAwesomeIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: 'white',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ))}

          {/* Sensitivity Chip */}
          {selectedSensitivity && selectedSensitivity !== 'all' && (
            <Chip
              label={`Sensitivität: ${getSensitivityLabel(selectedSensitivity)}`}
              size="small"
              color="default"
              onDelete={handleRemoveSensitivity}
              icon={<LockIcon />}
            />
          )}

          {/* OMOP Status Chip */}
          {selectedOmopStatus && selectedOmopStatus !== 'all' && (
            <Chip
              label={`OMOP: ${getOmopStatusLabel(selectedOmopStatus)}`}
              size="small"
              color="default"
              onDelete={handleRemoveOmopStatus}
              icon={<CheckCircleIcon />}
            />
          )}
        </ActiveFiltersRow>
      )}
    </FilterBarContainer>
  );
};

export default CompactFilterBar;
